import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Form, Spinner } from 'react-bootstrap';
import { formatDate, formatDateTime } from '../../utils/dateFormatter';
import { TEXT, BRIEF, DETAIL_ROWS, MARKET, errorText, marketPriceSentence, reviewsCountText, plainFieldNames } from '../../config/listingPerformanceLabels';
import { getPlatformDisplayName } from '../../utils/platformUtils';
import { problemLabel, TEXT as VOICE_TEXT } from '../../config/guestVoiceLabels';
import { useReviewBrief, useRerunBrief } from '../../hooks/useListingPerformance';
import { formatFigure } from './parts';
// The brand's reversed avatar (white body for dark grounds — brand guide v2.2) on a purple circle (founder 23 Oct).
import agentIcon from '../../assets/images/branding/agent/agent-icon-dark.svg';

/**
 * «قراءة الأسبوع» — ONE card on a unit that needs review (founder 20 Oct: two text cards on the same week read
 * as duplicates). Top: the AI's suggestion and «أتفق» / «لا أتفق» — the only thing ops act on. Folded «عرض الأسباب»:
 * the four answers. Bottom, folded: the collection notes, with how many ⚠ they hold so a warning is never hidden.
 * The agent icon marks the AI's part only — the notes are what the collection saw, not the AI.
 * Every word of state comes from the server as a code (tasks/unit-performance-ai-brief.md A8); the AI's own
 * text is shown as it came, `dir="auto"`.
 */

const QUESTIONS = ['unitMoved', 'marketMoved', 'reviewsOrBadges', 'changesOrCalendar'];
// The two the AI answers (brief-v9). On a card with the strip only these stay folded — the system's market and
// reviews lines repeat the strip, the unit table and «ما يزعج الضيوف» (founder 23 Oct).
const AI_QUESTIONS = ['unitMoved', 'changesOrCalendar'];
// One icon per Monday question, so ops find "the reviews one" without reading every title.
const QUESTION_ICONS = {
  unitMoved: 'bi-graph-up',
  marketMoved: 'bi-buildings',
  reviewsOrBadges: 'bi-star',
  changesOrCalendar: 'bi-pencil-square'
};
const MAX_REASON = 500;

const Notice = ({ code }) => (
  <p className={`lp-brief__notice${code === 'built_on_earlier_data' ? ' lp-brief__notice--stale' : ''}`}>
    <i className="bi bi-exclamation-triangle" aria-hidden="true" /> {BRIEF.notices[code]}
  </p>
);

/** «أتفق» / «لا أتفق». The reason box opens only on «لا أتفق»; a second click replaces the first. */
const ReviewChips = ({ unitCode, brief }) => {
  const review = useReviewBrief();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const chosen = brief.review?.verdict || null;

  const send = async (verdict, why = null) => {
    setError(null);
    try {
      await review.mutateAsync({ unitCode, week: brief.week, verdict, reason: why, generatedAt: brief.generatedAt });
      setAsking(false);
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div className="lp-brief__review">
      <span className="lp-brief__ask" id={`lp-brief-ask-${unitCode}`}>{BRIEF.ask}</span>
      <div className="lp-brief__chips" role="group" aria-labelledby={`lp-brief-ask-${unitCode}`}>
        <button
          type="button"
          className={`lp-brief__chip${chosen === 'agree' ? ' is-chosen' : ''}`}
          aria-pressed={chosen === 'agree'}
          disabled={review.isPending}
          onClick={() => send('agree')}
        >
          <i className="bi bi-check-lg" aria-hidden="true" /> {BRIEF.agree}
        </button>
        <button
          type="button"
          className={`lp-brief__chip${chosen === 'disagree' ? ' is-chosen' : ''}`}
          aria-pressed={chosen === 'disagree'}
          disabled={review.isPending}
          onClick={() => { setReason(brief.review?.reason || ''); setAsking(true); }}
        >
          <i className="bi bi-x-lg" aria-hidden="true" /> {BRIEF.disagree}
        </button>
        {review.isPending && <Spinner animation="border" size="sm" className="lp-spin" />}
      </div>

      {asking && (
        <Form
          className="lp-brief__reason"
          onSubmit={(e) => { e.preventDefault(); send('disagree', reason.trim() || null); }}
        >
          <Form.Group controlId={`lp-brief-reason-${unitCode}`}>
            <Form.Label>{BRIEF.reasonLabel}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={reason}
              maxLength={MAX_REASON}
              placeholder={BRIEF.reasonPlaceholder}
              onChange={(e) => setReason(e.target.value)}
            />
          </Form.Group>
          <div className="lp-brief__reason-actions">
            <Button variant="outline-secondary" size="sm" onClick={() => setAsking(false)} disabled={review.isPending}>{BRIEF.cancel}</Button>
            <Button type="submit" variant="primary" size="sm" disabled={review.isPending}>{BRIEF.save}</Button>
          </div>
        </Form>
      )}

      {brief.review && !asking && (
        <div className="lp-brief__reviewed">
          <p className="lp-muted">{BRIEF.reviewedBy} {brief.review.by || '—'} — {formatDateTime(brief.review.at)}</p>
          {/* The reason on its own line — it is what the learning step reads. */}
          {brief.review.reason && (
            <p className="lp-brief__reviewed-reason">
              <span className="lp-muted">{BRIEF.reasonShown}:</span> <span dir="auto">{brief.review.reason}</span>
            </p>
          )}
        </div>
      )}
      {error && <p className="lp-error" role="alert">{error}</p>}
    </div>
  );
};

const F = BRIEF.facts;
const tallyText = (t) => (t ? ['up', 'down', 'same'].filter(k => t[k]).map(k => `${t[k]} ${F.moved[k]}`).join('، ') : '');

const money = (v) => formatFigure(v, Number.isInteger(v) ? 'count' : 'money');
const pct = (v) => formatFigure(v, 'pct1');

/**
 * The market lines the system writes — what the figures show, never a judgment (brief-v9). Our price against
 * similar units leads, on its own line (brief-v11); the other units' movement follows.
 */
const marketLines = (m) => [
  ...(m.price ? [`${F.marketPrice}: ${marketPriceSentence(m.price, { money, pct })}`] : []),
  [
    `${F.otherUnits}: ${tallyText(m.otherUnits) || F.unavailable}`,
    `${F.sameBuilding}: ${tallyText(m.sameBuilding) || F.unavailable}`,
    ...(m.vsSimilarListingsLastWeek ? [`${F.vsSimilar}: ${F.vs[m.vsSimilarListingsLastWeek]}`] : []),
    `${F.effect}: ${F.effects[m.effect]}`
  ].join(' · ')
];

/**
 * The past-result line the system writes (step 4): the newest finished change, only when its counted result carries a
 * warning — one line naming each. The AI never writes about a past result; the counts stay in the change's box.
 */
const PastResultLine = ({ result }) => {
  const reasons = (result.warnings || []).map(code => F.pastResult.warnings[code]).filter(Boolean);
  if (!reasons.length) return null;
  return (
    <p className="lp-brief__past">
      <i className="bi bi-info-circle" aria-hidden="true" />
      <span>{F.pastResult.lead(formatDate(result.date, { year: undefined }))}: {reasons.join('، ')}.</span>
      <span className="lp-brief__system-tag">{F.systemTag}</span>
    </p>
  );
};

/**
 * The changes line the system writes (step 4): this week's change-log entries with their dates and platforms, then the
 * open nights. Straight from the package the AI read — it can claim nothing the log does not hold.
 */
const CHANGE_TEXT_SHOWN = 90;
const changesLines = (c) => {
  const entries = (c.entries || []).map((e) => {
    const text = e.change.length > CHANGE_TEXT_SHOWN ? `${e.change.slice(0, CHANGE_TEXT_SHOWN)}…` : e.change;
    // Each Latin name isolated (U+2068 … U+2069, the text form of <bdi>) so the Arabic order holds (audit, 3 Nov).
    const where = e.platforms?.length ? ` (${e.platforms.map(p => `\u2068${getPlatformDisplayName(p)}\u2069`).join('، ')})` : '';
    return `${formatDate(e.date, { year: undefined })}: ${text}${where}`;
  });
  return [...(entries.length ? entries : [F.changes.none]), ...(c.openNights !== null && c.openNights !== undefined ? [F.changes.open(c.openNights)] : [])];
};

/** The reviews line the system writes — the week's new reviews, the overall rating and the badge, as facts. */
const reviewsLine = (r) => {
  let fresh = `${F.newReviews}: ${F.unavailable}`;
  if (r.newReviews === 0) fresh = F.noNewReviews;
  else if (r.newReviews) {
    fresh = `${F.newReviews}: ${r.newReviews}${r.newReviewsAverageStars !== null ? ` ${F.avgStars(formatFigure(r.newReviewsAverageStars, 'rating'))}` : ''}`;
  }
  const rating = r.rating !== null ? formatFigure(r.rating, 'rating') : F.unavailable;
  return [
    fresh,
    `${F.rating}: ${rating}${r.ratingVsPrior ? ` (${F.ratingMoved[r.ratingVsPrior]})` : ''}`,
    `${F.badge}: ${r.badges || F.unavailable}${r.badgesVsPrior ? ` (${F.badgeMoved[r.badgesVsPrior]})` : ''}`
  ].join(' · ');
};

// ─── Where we stand — the system's parts of the card (layer-2 build step 5) ─────────────────────────────────
// Only on a week judged with the similar-listings rule; every figure is the code's, never the AI's.
const S = BRIEF.strip;
const C = BRIEF.checks;
const V = BRIEF.voice;
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/** One tile: «ours مقابل similar», its source, and a tone the code chose (amber = under similar listings). */
const Tile = ({ label, tone, main, source }) => (
  <div className={`lp-strip__tile lp-strip__tile--${tone}`}>
    <span className="lp-strip__label">{label}</span>
    <span className="lp-strip__main">{main}</span>
    <span className="lp-strip__source">{source}</span>
  </div>
);

const pair = (ours, similar, kind) => (isNum(ours) && isNum(similar)
  ? <><bdi className="lp-num">{formatFigure(ours, kind)}</bdi> {S.vs} <bdi className="lp-num">{formatFigure(similar, kind)}</bdi></>
  : S.unavailable);

const priceMain = (p) => {
  if (!p || !isNum(p.gapPct)) return p ? (MARKET.status[p.status] || S.unavailable) : S.unavailable;
  const pct = <bdi className="lp-num">{formatFigure(Math.abs(p.gapPct), 'pct1')}</bdi>;
  if (p.status === 'above') return <>{MARKET.above} {pct}</>;
  if (p.status === 'below') return <>{MARKET.below} {pct}</>;
  return MARKET.status.at;
};

/** «مقابل الوحدات المشابهة» — three separate comparisons, never one standing in for another (consultant 23 Oct). */
const Strip = ({ strip }) => {
  const tone = (below) => (below === true ? 'weak' : below === false ? 'good' : 'missing');
  const priceTone = !strip.price || !isNum(strip.price.gapPct) ? 'missing' : strip.price.status === 'above' ? 'weak' : 'good';
  return (
    <section className="lp-strip" aria-label={S.title}>
      <h5 className="lp-strip__title">{S.title} <span className="lp-brief__system-tag">{S.systemTag}</span></h5>
      <div className="lp-strip__tiles">
        <Tile label={S.pageViews} tone={tone(strip.pageViews.below)} main={pair(strip.pageViews.ours, strip.pageViews.similar, 'count')} source={S.byAirbnb} />
        {strip.bookingRate && (
          <Tile label={S.bookingRate} tone={tone(strip.bookingRate.below)} main={pair(strip.bookingRate.ours, strip.bookingRate.similar, 'pct2')} source={S.byAirbnb} />
        )}
        <Tile
          label={S.price}
          tone={priceTone}
          main={priceMain(strip.price)}
          source={strip.price?.limitedEvidence ? `${S.byMarket} — ${S.limited}` : S.byMarket}
        />
      </div>
    </section>
  );
};

/** What a system-filled check says — the figures it read; a check the system cannot see says «تحقق على Airbnb». */
const checkDetail = (item) => {
  if (item.state === 'check') return item.code === 'price' ? C.priceNoComparison : ['price', 'reviews', 'badge', 'openNights'].includes(item.code) ? C.notRead : C.onAirbnb;
  if (item.code === 'reviews') {
    const n = item.count === 0 ? C.noReviews : reviewsCountText(item.count);
    const r = isNum(item.rating) ? formatFigure(item.rating, 'rating') : '';
    if (item.reason === 'no_average') return C.noAverage(n);
    if (item.reason === 'under_five') return C.underFive(n);
    if (item.reason === 'rating_low') return C.ratingLow(r, n);
    return C.ratingOk(r, n);
  }
  if (item.code === 'badge') return item.state === 'ok' ? C.badgeOk : C.badgeMissing;
  if (item.code === 'openNights') return item.state === 'warn' ? C.openFew : '';
  return '';
};

const CHECK_ICONS = { ok: 'bi-check-circle-fill', warn: 'bi-exclamation-triangle-fill', check: 'bi-square' };

/** «ما يُفحص» — at most 5, on a «الإعلان» or «تحقق» reading; constants + the system's marks, never AI text. */
const Checks = ({ checks }) => (
  <section className="lp-checks" aria-label={C.title}>
    <h5 className="lp-strip__title">{C.title} — {C.steps[checks.step]}</h5>
    <ul className="lp-checks__list">
      {checks.items.map(item => (
        <li key={item.code} className={`lp-checks__item lp-checks__item--${item.state}`}>
          <i className={`bi ${CHECK_ICONS[item.state]}`} aria-hidden="true" />
          <span className="lp-checks__name">{C.items[item.code]}</span>
          <span className="lp-checks__detail">{checkDetail(item)}</span>
        </li>
      ))}
    </ul>
  </section>
);

/**
 * «ما يزعج الضيوف» — the unit's top problem types from «صوت الضيف», counts only, never review text. One quiet panel:
 * why it shows, then each problem with how many stays and when it was last raised (an old complaint must not read
 * like last week's); nothing recorded is an answer too — the reviews do not explain the weakness.
 */
const GuestVoiceLine = ({ voice }) => (
  <section className="lp-voice-line" aria-label={V.title}>
    <div className="lp-voice-line__head">
      <span className="lp-voice-line__icon" aria-hidden="true"><i className="bi bi-chat-square-text" /></span>
      <h5 className="lp-strip__title">{V.title} <span className="lp-brief__system-tag">{V.source}</span></h5>
      {/* Nothing recorded → no link: «صوت الضيف» would open on ANOTHER unit (it shows the first unit with reviews). */}
      {voice.rows.length > 0 && (
        <Link className="lp-link lp-voice-line__open" to={`/ops/reviews?list=voice&unit=${encodeURIComponent(voice.unitId)}`}>{V.open}</Link>
      )}
    </div>
    {V.why[voice.trigger] && <p className="lp-voice-line__why">{V.why[voice.trigger]}</p>}
    {voice.rows.length === 0 ? (
      <p className="lp-voice-line__none"><i className="bi bi-check2-circle" aria-hidden="true" /> {V.none}</p>
    ) : (
      <ul className="lp-voice-line__list">
        {voice.rows.map(r => (
          <li key={r.topicKey} className="lp-voice-line__row">
            <span className="lp-voice-line__topic">{problemLabel(r.topicKey)}</span>
            <span className="lp-voice-line__meta">
              {VOICE_TEXT.stays(r.stays)}
              {r.lastAt && <> · {V.lastAt} {formatDate(r.lastAt, { year: undefined })}</>}
            </span>
            {r.repeat && <span className="lp-voice-line__tag">{V.repeat}</span>}
            {r.cameBackAfterFix && <span className="lp-voice-line__tag">{V.cameBack}</span>}
            {r.handled && <span className="lp-voice-line__tag lp-voice-line__tag--muted">{V.handled}</span>}
          </li>
        ))}
      </ul>
    )}
  </section>
);

/** The text of one question: the system's line where it writes one, else the model's answer. */
const answerOf = (brief, q) => {
  if (q === 'marketMoved' && brief.facts?.market) return { lines: marketLines(brief.facts.market), system: true };
  if (q === 'reviewsOrBadges' && brief.facts?.reviews) return { lines: [reviewsLine(brief.facts.reviews)], system: true };
  if (q === 'changesOrCalendar' && brief.facts?.changes) return { lines: changesLines(brief.facts.changes), system: true };
  return brief.answers?.[q]?.text ? { lines: [plainFieldNames(brief.answers[q].text)], system: false } : null;
};

/**
 * The answers and the gaps, folded. A card with the strip («لماذا هذا الاقتراح؟») keeps only the AI's two answers,
 * tagged as the AI's, and the gaps; an older card keeps the four questions as it was (`slim` false).
 */
const Reasons = ({ brief, slim }) => (
  <div className="lp-brief__reasons">
    <dl className="lp-brief__answers">
      {(slim ? AI_QUESTIONS : QUESTIONS).map(q => {
        // The slim card keeps the AI's answer and the system's changes line (step 4).
        const a = slim && q !== 'changesOrCalendar'
          ? (brief.answers?.[q]?.text ? { lines: [plainFieldNames(brief.answers[q].text)], system: false } : null)
          : answerOf(brief, q);
        if (!a) return null;
        return (
          <div key={q} className="lp-brief__qa">
            <dt>
              <span className="lp-brief__qa-icon" aria-hidden="true"><i className={`bi ${QUESTION_ICONS[q]}`} /></span>
              {BRIEF.questions[q]}
              {a.system && <span className="lp-brief__system-tag">{F.systemTag}</span>}
              {slim && !a.system && <span className="lp-brief__ai-tag">{BRIEF.aiTag}</span>}
            </dt>
            <dd dir="auto">{a.lines.map((line, i) => <span key={i} className="lp-brief__line">{line}</span>)}</dd>
          </div>
        );
      })}
    </dl>
    {brief.gaps?.length > 0 && (
      <div className="lp-brief__gaps">
        <span className="lp-brief__gaps-title">{BRIEF.gaps}</span>
        <ul>{brief.gaps.map((g, i) => <li key={i} dir="auto">{plainFieldNames(g)}</li>)}</ul>
      </div>
    )}
  </div>
);

/** The AI's part of the card, in every state the server can send. */
const AiReading = ({ unitCode, brief }) => {
  const rerun = useRerunBrief();
  const [whyOpen, setWhyOpen] = useState(false);
  const [error, setError] = useState(null);
  const shown = brief.status === 'done' && brief.answers;
  const slim = Boolean(brief.facts?.strip);
  const missingData = brief.notices?.includes('missing_data');
  // brief-v10 / v11: a price idea the data cannot carry is never shown — the fixed line says why instead.
  const blockedPrice = ['no_price_comparison', 'weak_price_comparison', 'market_not_above'].find(c => brief.notices?.includes(c)) || null;
  // The system lock (step 4): a change still being measured — the card says to wait, and when its result is due.
  const waiting = brief.notices?.includes('change_running') && brief.waitFor ? brief.waitFor : null;

  const askAgain = async () => {
    setError(null);
    try {
      await rerun.mutateAsync({ unitCode, week: brief.week });
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div className="lp-brief__ai">
      <div className="lp-brief__head">
        <span className="lp-brief__avatar" aria-hidden="true"><img src={agentIcon} alt="" /></span>
        <div>
          <span className="lp-brief__ai-title" title={BRIEF.aiHint}>{BRIEF.ai}</span>
          <span className="lp-muted lp-brief__meta">
            {BRIEF.week} {formatDate(brief.week, { year: undefined })}
            {brief.dataReadAt && <> — {BRIEF.dataReadAt} {formatDateTime(brief.dataReadAt)}</>}
          </span>
        </div>
      </div>

      {brief.status === 'pending' && (
        <p className="lp-brief__state" role="status"><Spinner animation="border" size="sm" className="lp-spin" /> {BRIEF.status.pending}</p>
      )}
      {(brief.status === 'none' || brief.status === 'failed') && <p className="lp-brief__state">{BRIEF.status[brief.status]}</p>}
      {/* Step 4: a failed card says why — never looks like a reading that found nothing. */}
      {brief.status === 'failed' && brief.notices.filter(c => c.startsWith('failed_')).map(c => <Notice key={c} code={c} />)}
      {missingData && (
        <p className="lp-brief__state">
          {BRIEF.notices.missing_data}
          {brief.missing?.length > 0 && <>: {brief.missing.map(k => DETAIL_ROWS[k]?.label || k).join('، ')}</>}
        </p>
      )}
      {brief.canRerun && (
        <Button variant="outline-primary" size="sm" onClick={askAgain} disabled={rerun.isPending}>
          {rerun.isPending ? <Spinner animation="border" size="sm" className="lp-spin" /> : <i className="bi bi-arrow-repeat" aria-hidden="true" />} {BRIEF.rerun}
        </Button>
      )}

      {shown && (
        <>
          <p className="lp-brief__suggestion">
            <span className={`lp-brief__kind lp-brief__kind--${brief.action.kind}`}>{waiting ? BRIEF.waitChip : BRIEF.actions[brief.action.kind]}</span>
            <span dir="auto">
              {waiting
                ? BRIEF.waitLine(formatDate(waiting.date, { year: undefined }), formatDate(waiting.readyOn, { year: undefined }))
                : blockedPrice ? BRIEF.notices[blockedPrice] : plainFieldNames(brief.action.text)}
            </span>
          </p>
          {brief.notices.filter(c => c !== 'missing_data' && c !== blockedPrice && c !== 'change_running').map(c => <Notice key={c} code={c} />)}
          {brief.facts?.pastResult && <PastResultLine result={brief.facts.pastResult} />}
          {brief.canReview && <ReviewChips unitCode={unitCode} brief={brief} />}
          {brief.facts?.strip && <Strip strip={brief.facts.strip} />}
          {brief.checks && <Checks checks={brief.checks} />}
          {brief.guestVoice && <GuestVoiceLine voice={brief.guestVoice} />}
          <Button
            variant="link"
            size="sm"
            className="lp-link lp-brief__why-toggle"
            aria-expanded={whyOpen}
            onClick={() => setWhyOpen(o => !o)}
          >
            <i className={`bi ${whyOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden="true" />{' '}
            {slim ? (whyOpen ? BRIEF.hideWhySuggestion : BRIEF.whySuggestion) : (whyOpen ? BRIEF.hideWhy : BRIEF.why)}
          </Button>
          {whyOpen && <Reasons brief={brief} slim={slim} />}
        </>
      )}
      {error && <p className="lp-error" role="alert">{error}</p>}
    </div>
  );
};

/** The collection's notes, folded. The fold line says how many ⚠ they hold. */
const NotesFold = ({ notes, readOn }) => {
  const [open, setOpen] = useState(false);
  if (!notes && !readOn) return null;
  const warnings = notes ? (notes.match(/⚠/g) || []).length : 0;
  return (
    <div className="lp-brief__notes">
      <button type="button" className="lp-brief__notes-toggle" aria-expanded={open} onClick={() => setOpen(o => !o)} disabled={!notes}>
        {notes && <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden="true" />}
        <span className="lp-notes__label">{TEXT.notes}</span>
        {readOn && <span className="lp-muted">— {TEXT.readOn} {formatDate(readOn)}</span>}
        {warnings > 0 && <span className="lp-brief__warn"><i className="bi bi-exclamation-triangle" aria-hidden="true" /> {BRIEF.notesCount(warnings)}</span>}
      </button>
      {open && notes && <p className="lp-brief__notes-text">{notes}</p>}
    </div>
  );
};

const WeekReading = ({ unitCode, brief, notes, readOn }) => (
  <section className="lp-brief" aria-labelledby={`lp-brief-title-${unitCode}`}>
    <h4 id={`lp-brief-title-${unitCode}`} className="lp-section-title lp-brief__title">{BRIEF.card}</h4>
    <AiReading unitCode={unitCode} brief={brief} />
    <NotesFold notes={notes} readOn={readOn} />
  </section>
);

export default WeekReading;
